import classNames from "classnames";
import { memo, ReactNode, useMemo } from "react";
import { SettingsSearchSection } from "../settings-search/settings-search-section";
import {
  SettingSectionWrapperContext,
  SettingSectionWrapperContextValue,
} from "./setting-section-wrapper-context";
import * as css from "./setting-wrapper.module.css";
import { useHighlightTarget } from "./use-highlight-target";

export interface SettingSectionWrapperProps {
  /** Group id matched against the `?group` query param. */
  id: string;
  children: ReactNode;
  className?: string;
  /** Use the compact (tight) highlight style, and pass it to descendant settings. */
  compact?: boolean;
  /** Highlight only, never scroll into view. */
  disableAutoScroll?: boolean;
  /** Override the default `{ behavior: "smooth", block: "center" }`. */
  scrollOptions?: ScrollIntoViewOptions;
}

/**
 * Wraps a group/section of settings. Always highlightable, and publishes its id
 * so descendant `<SettingWrapper>`s know their group. Deeplink it with
 * `?group=<id>`; when a `?setting` is also present, the matching setting scrolls
 * and this group only glows as the container.
 */
export const SettingSectionWrapper = memo(function SettingSectionWrapper({
  id,
  children,
  className,
  compact = false,
  disableAutoScroll,
  scrollOptions,
}: SettingSectionWrapperProps) {
  const { ref, isActive, isPulsing, isSubdued, onAnimationEnd } =
    useHighlightTarget(
      { kind: "group", id },
      { disableAutoScroll, scrollOptions },
    );

  const contextValue = useMemo<SettingSectionWrapperContextValue>(
    () => ({ groupId: id, compact }),
    [id, compact],
  );

  return (
    <SettingSectionWrapperContext.Provider value={contextValue}>
      <SettingsSearchSection>
        <div
          className={classNames(
            css.wrapper,
            isActive && (isSubdued ? css.activeSubdued : css.active),
            isActive && isPulsing && css.pulse,
            compact && css.compact,
            className,
          )}
          onAnimationEnd={onAnimationEnd}
          ref={ref}
        >
          {children}
        </div>
      </SettingsSearchSection>
    </SettingSectionWrapperContext.Provider>
  );
});
